import { NAP } from "@/lib/nap";

const ORG_ID = `${NAP.siteUrl}/#organization`;
const LOGO_URL = `${NAP.siteUrl}/img/logo.svg`;

const organization = {
  "@type": ["Organization", "RealEstateAgent"],
  "@id": ORG_ID,
  name: NAP.brand,
  alternateName: NAP.legalName,
  url: NAP.siteUrl,
  logo: LOGO_URL,
  telephone: NAP.phone,
  email: NAP.email,
  address: {
    "@type": "PostalAddress",
    streetAddress: NAP.streetAddress,
    addressLocality: NAP.addressLocality,
    addressRegion: NAP.addressRegion,
    postalCode: NAP.postalCode,
    addressCountry: NAP.addressCountry,
  },
};

/**
 * Build a BlogPosting schema for /blog/[slug]. Author info is sourced
 * from the joined users row on the API side (object with name, role,
 * bio). Optional fields (datePublished, image) are only emitted when
 * truthy so the resulting JSON-LD passes Rich Results validation.
 */
export function buildBlogPostSchema(blog) {
  if (!blog) return { "@context": "https://schema.org", "@graph": [organization] };
  const slug = blog.slug || "";
  const postId = `${NAP.siteUrl}/blog/${slug}#blogposting`;
  const postUrl = `${NAP.siteUrl}/blog/${slug}`;

  const authorObj = blog.author && typeof blog.author === "object" ? blog.author : null;
  const authorName = authorObj?.name || "Siddhivinayak Group";
  const organizationalAuthor = /\b(team|group|editorial|desk)\b/i.test(authorName);
  const authorSchema = {
    "@type": organizationalAuthor ? "Organization" : "Person",
    name: authorName,
    ...(!organizationalAuthor && authorObj?.credentials
      ? { jobTitle: authorObj.credentials }
      : {}),
    ...(authorObj?.bio ? { description: authorObj.bio } : {}),
    ...(authorObj?.avatar_url ? { image: authorObj.avatar_url } : {}),
    ...(Array.isArray(authorObj?.same_as) && authorObj.same_as.length
      ? { sameAs: authorObj.same_as }
      : {}),
    ...(organizationalAuthor
      ? { parentOrganization: { "@id": ORG_ID } }
      : { worksFor: { "@type": "Organization", name: NAP.brand, "@id": ORG_ID } }),
  };

  const blogPosting = {
    "@type": "BlogPosting",
    "@id": postId,
    headline: blog.title || "",
    description: blog.fullDescription || blog.shortDescription || "",
    url: postUrl,
    ...(blog.featured_image ? { image: blog.featured_image } : {}),
    datePublished: blog.published_at || undefined,
    dateModified: blog.updated_at || blog.published_at || undefined,
    author: authorSchema,
    publisher: { "@id": ORG_ID },
    isPartOf: { "@type": "Blog", name: "Siddhivinayak Group Blog" },
    inLanguage: "en-IN",
  };

  // Drop undefined keys (datePublished/dateModified may be missing).
  for (const k of Object.keys(blogPosting)) {
    if (blogPosting[k] === undefined) delete blogPosting[k];
  }

  const breadcrumb = {
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: NAP.siteUrl },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: `${NAP.siteUrl}/blog`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: blog.title || "Post",
        item: postUrl,
      },
    ],
  };

  const faqItems = Array.isArray(blog.faqs)
    ? blog.faqs
        .filter((faq) => faq?.question && faq?.answer)
        .map((faq) => ({
          "@type": "Question",
          name: String(faq.question),
          acceptedAnswer: {
            "@type": "Answer",
            text: String(faq.answer),
          },
        }))
    : [];

  const faqPage = faqItems.length
    ? { "@type": "FAQPage", mainEntity: faqItems }
    : null;

  return {
    "@context": "https://schema.org",
    "@graph": [organization, blogPosting, breadcrumb, ...(faqPage ? [faqPage] : [])],
  };
}
